import React, { useEffect, useMemo, useReducer } from "react";
import type { FC } from "react";
import { BulkExportContext } from "./BulkExportProvider.context";
import { bulkExportReducer } from "./BulkExportProvider.reducer";
import { useDataSourcesQuery, useSavedExportSettingsQuery } from "../../api";
import { BulkExportFormat } from "../../types";
import { decodeToken } from "../../../auth";
import { IBulkExportContextState, IBulkExportContextValue, IBulkExportContextActions, BulkExportTab } from "./BulkExportProvider.types";
import { isEqual, cloneDeep, sortBy } from "lodash";
import { BulkExportFormatEnum } from "../../api/mutations/saveBulkExportState/saveBulkExportState.types";
import { virtualConfiguration } from "../../components/BulkExportStepper";
import { virtualFrequency } from "../../components/BulkExportStepper/BulkExportStepper.constants";

const initialState: IBulkExportContextState = {
  format: BulkExportFormat.XLSX,
  tab: BulkExportTab.New,
  dimensions: [],
  settingsWasModified: false,
  showSaveNotification: false,
  currentFavouriteSettings: undefined,
  settingsModifiedForFavourites: false,
  defaultCalendarDimension: undefined,
  cubeId: "",
};

export const BulkExportProvider: FC = (props) => {
  const { children } = props;

  const [state, dispatch] = useReducer(bulkExportReducer, initialState);
  const { isDataSourcesLoading, dataSources } = useDataSourcesQuery();
  const { isSavedExportSettingsLoading, savedExportSettings } = useSavedExportSettingsQuery();
  // Controls value initialization
  useEffect(() => {
    const { email } = decodeToken() ?? {};
    if (email) {
      dispatch({ type: "setEmail", payload: email });
    }
  }, []);
  useEffect(() => {
    if (!isDataSourcesLoading && dataSources) {
      const defaultDataSource = dataSources.find((it) => it.identifier === "facts_harm") ?? dataSources[0];
      if (defaultDataSource !== undefined) {
        dispatch({ type: "setDataSourceId", payload: defaultDataSource.id });
      }
    }
  }, [isDataSourcesLoading, dataSources]);
  useEffect(() => {
    if (!isSavedExportSettingsLoading && savedExportSettings && !state.savedExportSettingsId) {
      const defaultSavedSettings = sortBy(savedExportSettings, ["parentId", "name"])[0];
      if (defaultSavedSettings !== undefined && !state.savedExportSettingsId) {
        dispatch({
          type: "setSavedExportSettingsId",
          payload: {
            id: defaultSavedSettings.id,
            dimensions: [],
            format: BulkExportFormat.XLSX,
          },
        });
      }
    }
  }, [isSavedExportSettingsLoading, savedExportSettings]);
  useEffect(() => {
    const settings = savedExportSettings?.find((el) => el.id === state.savedExportSettingsId);
    if (settings) {
      dispatch({
        type: "setCurrentSavedSettings",
        payload: settings,
      });
    }
  }, [savedExportSettings, state.savedExportSettingsId]);

  useEffect(() => {
    if (!isSavedExportSettingsLoading && savedExportSettings && state.savedExportSettingsId && state.tab === 1) {
      const currentSettings = savedExportSettings?.find((el) => el.id === state.savedExportSettingsId);
      const configuration: (string | number)[] = [];
      currentSettings &&
        currentSettings.dimensions &&
        currentSettings.dimensions.forEach((dim) => {
          if (dim.fieldName) {
            configuration.push(`${dim.id}.name`);
          }
        });
      currentSettings &&
        currentSettings.measures &&
        currentSettings.measures.forEach((dim) => {
          configuration.push(`${dim.id}`);
        });

      const sortedSavedDim = cloneDeep(currentSettings?.dimensions!) || [];
      sortedSavedDim.push({ fieldName: "", id: virtualConfiguration.identifier, isSelected: true, selection: configuration.sort() });

      const sortedStateDim = cloneDeep(state.dimensions!) || [];
      const virtualFrequencySorted = sortedStateDim.find((dim) => dim.id === virtualFrequency.hierarchyIdentifier);
      if (!!virtualFrequencySorted) {
        const currentSelection = +virtualFrequencySorted.selection[0].toString().split("")[0];
        if (typeof currentSelection === "number" && !isNaN(currentSelection)) {
          sortedSavedDim.push({ fieldName: "", id: virtualFrequency.hierarchyIdentifier, isSelected: true, selection: [currentSelection] });
        }
      }

      // FIXME: разобраться почему этого объекта нет calendar.default
      const missingObject = sortedStateDim.find((el) => el.id === "calendar.default");

      if (missingObject && !sortedSavedDim.some((el) => el.id === "calendar.default")) {
        sortedSavedDim.push({ ...missingObject });
      }

      const sortedState = sortBy(
        sortedStateDim.map((el) => ({ id: el.id, selection: el.selection.sort(), isSelected: true })),
        "id"
      )
        .filter((f) => f.id !== "value")
        .filter((f) => f.selection.length !== 0);
      const sortedSaved = sortBy(
        sortedSavedDim.map((el) => ({ id: el.id, selection: el.selection.sort(), isSelected: true })),
        "id"
      )
        .filter((f) => f.id !== "value")
        .filter((f) => f.selection.length !== 0);

      const isFormatUnchanged = BulkExportFormatEnum[state?.format] === currentSettings?.format;
      const isModified = !isEqual(sortedState, sortedSaved) || !isFormatUnchanged;

      dispatch({
        type: "setSettingsWasModified",
        payload: isModified,
      });
    }

    if (savedExportSettings?.length === 0 || state.savedExportSettingsId === undefined) {
      dispatch({
        type: "setSettingsWasModified",
        payload: false,
      });
    }
  }, [isSavedExportSettingsLoading, savedExportSettings, state.savedExportSettingsId, state.format, state.tab, state.dimensions]);

  const actions = useMemo<IBulkExportContextActions>(
    () => ({
      setDataSourceId: (payload) => dispatch({ type: "setDataSourceId", payload }),
      setEmail: (payload) => dispatch({ type: "setEmail", payload }),
      setFormat: (payload) => dispatch({ type: "setFormat", payload }),
      setDimension: (payload) => dispatch({ type: "setDimension", payload }),
      setTab: (payload) => dispatch({ type: "setTab", payload }),
      setSavedExportSettingsId: (payload) => dispatch({ type: "setSavedExportSettingsId", payload }),
      setDefaultDimensions: (payload) => dispatch({ type: "setDefaultDimensions", payload }),
      setSettingsWasModified: (payload) => dispatch({ type: "setSettingsWasModified", payload }),
      setShowSaveNotification: (payload) => dispatch({ type: "setShowSaveNotification", payload }),
      setActionAfterSaveCancel: (payload) => dispatch({ type: "setActionAfterSaveCancel", payload }),
      setCurrentSavedSettings: (payload) => dispatch({ type: "setCurrentSavedSettings", payload }),
      setCurrentFavouriteSettings: (payload) => dispatch({ type: "setCurrentFavouriteSettings", payload }),
      setDimensionCurrentFavourite: (payload) => dispatch({ type: "setDimensionCurrentFavourite", payload }),
      setFormatCurrentFavourite: (payload) => dispatch({ type: "setFormatCurrentFavourite", payload }),
      setSettingsModifiedForFavourites: (payload) => dispatch({ type: "setSettingsModifiedForFavourites", payload }),
      setDefaultCalendarDimension: (payload) => dispatch({ type: "setDefaultCalendarDimension", payload }),
      setCubeId: (payload) => dispatch({ type: "setCubeId", payload }),
    }),
    []
  );
  const contextValue = useMemo(() => {
    if (state === undefined) {
      return undefined;
    }
    return { state, actions } as IBulkExportContextValue;
  }, [state, actions]);

  if (isDataSourcesLoading || contextValue === undefined) {
    return null; // TODO: Add loader
  }

  return <BulkExportContext.Provider value={contextValue}>{children}</BulkExportContext.Provider>;
};
